import React, { useEffect, useMemo, useState } from 'react';
import plantillasContratoService from '../services/plantillasContratoService';
import contratosEmpleadoService from '../services/contratosEmpleadoService';
import atributoService from '../services/atributoService';
import { obtenerFirmaEmpresaBase64 } from '../services/firmaEmpresaService';
import { obtenerLogoEmpresaParaVista, obtenerLogoEmpresaBase64 } from '../services/logoEmpresaService';
import { exportarContratoEmpleadoPdf } from '../utils/contratoEmpleadoPdf';
import { resolverStackFuenteContrato, obtenerEtiquetaFuenteContrato } from '../utils/contratoFuentes';
import { construirContenidoVistaPreviaContratoResuelto } from '../utils/plantillaContratoPreview';
import ContratoVistaPreviaPaginada from './ContratoVistaPreviaPaginada';
import {
  detectarParesSolapados,
  buscarContratosSolapados,
  mensajeConfirmacionSolape,
  calcularFechaFinContrato,
} from '../utils/contratoSolapamiento';
import {
  calcularMesesYDias,
  esPlazoSoloMeses,
  formatearPlazoTexto,
  plazoDesdeContrato,
} from '../utils/contratoPlazo';
import {
  contratoEstaVencido,
  fechaHoyLocal,
  fechaInicioRenovacion,
} from '../utils/contratoRenovacion';

const formatearFecha = (fecha) => {
  if (!fecha) return '-';
  const texto = String(fecha).split('T')[0];
  const [y, m, d] = texto.split('-');
  if (!y || !m || !d) return texto;
  return `${d}/${m}/${y}`;
};

const CamposPlazoContrato = ({
  modoPlazo,
  onCambiarModo,
  fechaInicio,
  plazoMeses,
  onPlazoMeses,
  fechaFin,
  onFechaFin,
}) => {
  const resumen = (() => {
    if (!fechaInicio) return { texto: '', fin: '' };
    if (modoPlazo === 'fecha_fin') {
      if (!fechaFin) return { texto: '', fin: '' };
      const { meses, dias } = calcularMesesYDias(fechaInicio, fechaFin);
      return {
        texto: formatearPlazoTexto(meses, dias),
        fin: fechaFin,
        meses,
        dias,
      };
    }
    const meses = plazoMeses ? Number(plazoMeses) : 0;
    const fin = calcularFechaFinContrato(fechaInicio, meses, 0);
    return {
      texto: formatearPlazoTexto(meses, 0),
      fin,
      meses,
      dias: 0,
    };
  })();

  return (
    <>
      <div className="lg:col-span-4">
        <label className="block text-sm font-medium text-gray-700 mb-2">Cómo definir el plazo</label>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={() => onCambiarModo('meses')}
            className={`px-3 py-1.5 text-sm rounded-lg border ${
              modoPlazo === 'meses'
                ? 'bg-indigo-600 text-white border-indigo-600'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
          >
            En meses
          </button>
          <button
            type="button"
            onClick={() => onCambiarModo('fecha_fin')}
            className={`px-3 py-1.5 text-sm rounded-lg border ${
              modoPlazo === 'fecha_fin'
                ? 'bg-indigo-600 text-white border-indigo-600'
                : 'bg-white text-gray-700 border-gray-300 hover:bg-gray-50'
            }`}
          >
            Fecha de fin exacta
          </button>
        </div>
      </div>
      {modoPlazo === 'meses' ? (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Plazo en meses</label>
          <input
            type="number"
            min="1"
            max="600"
            value={plazoMeses}
            onChange={(e) => onPlazoMeses(e.target.value)}
            placeholder="Ej: 6"
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
          />
        </div>
      ) : (
        <div>
          <label className="block text-sm font-medium text-gray-700 mb-2">Fecha de fin *</label>
          <input
            type="date"
            value={fechaFin}
            min={fechaInicio || undefined}
            onChange={(e) => onFechaFin(e.target.value)}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
          />
        </div>
      )}
      {resumen.texto && (
        <div className="lg:col-span-3 text-sm text-indigo-800 bg-indigo-50 border border-indigo-100 rounded-lg px-3 py-2">
          El contrato dura <strong>{resumen.texto}</strong>
          {resumen.fin ? <> y vence el <strong>{formatearFecha(resumen.fin)}</strong></> : null}.
        </div>
      )}
    </>
  );
};

const badgeEstado = (estado) => {
  const normalizado = estado === 'asignado' ? 'pendiente' : estado;
  if (normalizado === 'firmado') {
    return 'bg-green-100 text-green-800';
  }
  return 'bg-amber-100 text-amber-800';
};

const VistaPreviaContratoPaginada = ({ titulo, subtitulo, htmlContenido, logoSrc, fuente }) => {
  if (!htmlContenido) return null;

  return (
    <div className="border border-gray-200 rounded-lg overflow-hidden">
      <div className="px-4 py-3 bg-slate-100 border-b border-gray-200">
        <h3 className="font-semibold text-gray-900">{titulo}</h3>
        {subtitulo && <p className="text-sm text-gray-600 mt-0.5">{subtitulo}</p>}
        {fuente && (
          <p className="text-xs text-gray-500 mt-0.5">
            Fuente: {obtenerEtiquetaFuenteContrato(fuente)} · Hojas A4 aproximadas
          </p>
        )}
      </div>
      <div className="p-4 bg-slate-100 flex justify-center overflow-x-auto min-w-0">
        <ContratoVistaPreviaPaginada
          key={logoSrc || 'sin-logo'}
          htmlContenido={htmlContenido}
          logoSrc={logoSrc}
          fontFamily={resolverStackFuenteContrato(fuente)}
        />
      </div>
    </div>
  );
};

const AsignarContratoModal = ({
  isOpen,
  onClose,
  empleado,
  empresa,
  empresaId,
  onSuccess,
  soloHistorial = false,
  /** Id o datos del contrato vencido a renovar (abre formulario prellenado). */
  contratoARenovar = null,
  onRenovacionAplicada = null,
}) => {
  const [plantillas, setPlantillas] = useState([]);
  const [tiposContrato, setTiposContrato] = useState([]);
  const [tipoContrato, setTipoContrato] = useState('');
  const [contratosAsignados, setContratosAsignados] = useState([]);
  const [plantillaId, setPlantillaId] = useState('');
  const [fechaFirma, setFechaFirma] = useState('');
  const [fechaInicio, setFechaInicio] = useState('');
  const [plazoMeses, setPlazoMeses] = useState('');
  const [fechaFin, setFechaFin] = useState('');
  const [modoPlazo, setModoPlazo] = useState('meses');
  const [lugarFirma, setLugarFirma] = useState('');
  const [vistaPrevia, setVistaPrevia] = useState(null);
  const [contratoDetalle, setContratoDetalle] = useState(null);
  const [contratoEditando, setContratoEditando] = useState(null);
  const [mostrarFormularioNuevo, setMostrarFormularioNuevo] = useState(false);
  const [renovandoDesde, setRenovandoDesde] = useState(null);
  const [loading, setLoading] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [generandoPdf, setGenerandoPdf] = useState(false);
  const [error, setError] = useState('');
  const [logoEmpresaUrl, setLogoEmpresaUrl] = useState(null);

  const { pares: paresSolapados, idsEnSolape } = useMemo(
    () => detectarParesSolapados(contratosAsignados),
    [contratosAsignados]
  );

  const obtenerFechaInicioDefault = () => {
    const historial = empleado?.historial_laboral?.[0];
    if (!historial?.fecha_ingreso) return '';
    return String(historial.fecha_ingreso).split('T')[0];
  };

  const construirPayload = (idPlantilla = plantillaId) => {
    const { meses, dias } = resolverPlazoPayload();
    return {
      empleado_id: empleado.id,
      plantilla_contrato_id: Number(idPlantilla),
      fecha_firma: fechaFirma || null,
      lugar_firma: lugarFirma || null,
      fecha_inicio: fechaInicio || null,
      plazo_meses: meses,
      plazo_dias: dias,
      fecha_fin: modoPlazo === 'fecha_fin' ? (fechaFin || null) : calcularFechaFinContrato(fechaInicio, meses, dias),
    };
  };

  const construirPayloadEdicion = () => {
    const { meses, dias } = resolverPlazoPayload();
    return {
      plantilla_contrato_id: contratoEditando?.plantilla_contrato_id,
      fecha_firma: fechaFirma || null,
      lugar_firma: lugarFirma || null,
      fecha_inicio: fechaInicio || null,
      plazo_meses: meses,
      plazo_dias: dias,
      fecha_fin: modoPlazo === 'fecha_fin' ? (fechaFin || null) : calcularFechaFinContrato(fechaInicio, meses, dias),
    };
  };

  const resolverPlazoPayload = () => {
    if (modoPlazo === 'fecha_fin' && fechaInicio && fechaFin) {
      return calcularMesesYDias(fechaInicio, fechaFin);
    }
    return {
      meses: plazoMeses !== '' && plazoMeses != null ? Number(plazoMeses) : null,
      dias: 0,
    };
  };

  const cambiarModoPlazo = (nuevoModo) => {
    if (nuevoModo === modoPlazo) return;
    if (nuevoModo === 'fecha_fin') {
      const calculada = calcularFechaFinContrato(fechaInicio, plazoMeses, 0);
      setFechaFin(calculada || fechaFin || '');
    } else if (fechaInicio && fechaFin) {
      const { meses } = calcularMesesYDias(fechaInicio, fechaFin);
      setPlazoMeses(meses > 0 ? String(meses) : '');
    }
    setModoPlazo(nuevoModo);
  };

  const cargarContratos = async () => {
    const response = await contratosEmpleadoService.obtenerPorEmpleado(empleado.id);
    if (response.success) {
      setContratosAsignados(response.data);
      return response.data;
    }
    setContratosAsignados([]);
    return [];
  };

  const resetFormularioNuevo = (tiposDisponibles = tiposContrato, plantillasCargadas = plantillas) => {
    setPlantillaId('');
    setVistaPrevia(null);
    setFechaFirma(new Date().toISOString().split('T')[0]);
    setFechaInicio(obtenerFechaInicioDefault());
    setPlazoMeses('');
    setFechaFin('');
    setModoPlazo('meses');
    setLugarFirma('');
    setRenovandoDesde(null);

    const primerTipoConPlantillas = tiposDisponibles.find((tipo) =>
      plantillasCargadas.some((item) => item.activo !== false && item.tipo === tipo)
    );
    setTipoContrato(primerTipoConPlantillas || tiposDisponibles[0] || '');
  };

  const iniciarRenovacion = async (contratoBase, plantillasCargadas = plantillas) => {
    if (!contratoBase || soloHistorial) return;

    let contrato = contratoBase;
    if (!contrato.plantilla_contrato_id && contrato.id) {
      const response = await contratosEmpleadoService.obtenerContrato(contrato.id);
      if (response.success) {
        contrato = response.data;
      }
    }

    const fin =
      (contrato.fecha_fin ? String(contrato.fecha_fin).split('T')[0] : '') ||
      calcularFechaFinContrato(
        contrato.fecha_inicio,
        contrato.plazo_meses,
        contrato.plazo_dias
      ) ||
      null;
    const inicioRenovacion =
      fechaInicioRenovacion(fin) || fechaHoyLocal();

    setContratoEditando(null);
    setContratoDetalle(null);
    setRenovandoDesde({
      id: contrato.id,
      titulo: contrato.titulo,
      fecha_fin: fin,
    });
    setMostrarFormularioNuevo(true);
    setTipoContrato(contrato.tipo || contrato.tipo_contrato || '');
    setPlantillaId(
      contrato.plantilla_contrato_id ? String(contrato.plantilla_contrato_id) : ''
    );
    setPlazoMeses(contrato.plazo_meses != null ? String(contrato.plazo_meses) : '');
    const diasRenovacion = Number(contrato.plazo_dias) || 0;
    if (diasRenovacion > 0) {
      setModoPlazo('fecha_fin');
      setFechaFin(calcularFechaFinContrato(inicioRenovacion, contrato.plazo_meses, diasRenovacion) || '');
    } else {
      setModoPlazo('meses');
      setFechaFin('');
    }
    setFechaInicio(inicioRenovacion);
    setFechaFirma(fechaHoyLocal());
    setLugarFirma(contrato.lugar_firma || '');
    setError('');

    const idPlantilla = contrato.plantilla_contrato_id;
    if (idPlantilla && inicioRenovacion) {
      setLoading(true);
      const preview = await contratosEmpleadoService.vistaPrevia({
        empleado_id: empleado.id,
        plantilla_contrato_id: Number(idPlantilla),
        fecha_firma: fechaHoyLocal(),
        lugar_firma: contrato.lugar_firma || null,
        fecha_inicio: inicioRenovacion,
        plazo_meses: contrato.plazo_meses != null ? Number(contrato.plazo_meses) : null,
        plazo_dias: Number(contrato.plazo_dias) || 0,
        fecha_fin: diasRenovacion > 0
          ? calcularFechaFinContrato(inicioRenovacion, contrato.plazo_meses, diasRenovacion)
          : null,
      });
      setLoading(false);
      if (preview.success) {
        setVistaPrevia(preview.data);
      }
    }

    // Asegurar que el tipo exista en el listado de plantillas
    const plantilla = (plantillasCargadas || []).find(
      (p) => Number(p.id) === Number(idPlantilla)
    );
    if (plantilla?.tipo) {
      setTipoContrato(plantilla.tipo);
    }
  };

  useEffect(() => {
    if (!isOpen || !empleado?.id || !empresaId) return;

    const cargarDatos = async () => {
      setLoading(true);
      setError('');
      setVistaPrevia(null);
      setContratoDetalle(null);
      setContratoEditando(null);
      setRenovandoDesde(null);
      resetFormularioNuevo();

      const [plantillasResponse, atributosResponse, contratos, logoSrc] = await Promise.all([
        soloHistorial
          ? Promise.resolve({ success: true, data: [] })
          : plantillasContratoService.obtenerPlantillas(empresaId),
        soloHistorial
          ? Promise.resolve({ success: true, data: null })
          : atributoService.obtenerConfiguracionCompleta(empresaId),
        cargarContratos(),
        obtenerLogoEmpresaParaVista(empresaId),
      ]);

      const plantillasCargadas = plantillasResponse.success ? plantillasResponse.data : [];
      setPlantillas(plantillasCargadas);

      const subAtributos = atributosResponse.success
        ? atributosResponse.data?.sub_atributos?.['Tipos de Contratación'] || []
        : [];
      const tiposCargados = subAtributos
        .map((item) => item.nombre || item.valor || item.descripcion)
        .filter(Boolean);
      setTiposContrato(tiposCargados);
      setLogoEmpresaUrl(logoSrc || null);
      resetFormularioNuevo(tiposCargados, plantillasCargadas);

      let renovarAplicado = false;
      if (!soloHistorial && contratoARenovar) {
        const idRenovar =
          typeof contratoARenovar === 'object'
            ? contratoARenovar.id || contratoARenovar.contrato_id
            : contratoARenovar;
        const desdeLista = (contratos || []).find(
          (c) => Number(c.id) === Number(idRenovar)
        );
        const base =
          desdeLista ||
          (typeof contratoARenovar === 'object' ? contratoARenovar : null);
        if (base) {
          await iniciarRenovacion(
            {
              ...base,
              id: base.id || base.contrato_id,
              plantilla_contrato_id:
                base.plantilla_contrato_id || desdeLista?.plantilla_contrato_id,
              plazo_meses: base.plazo_meses ?? desdeLista?.plazo_meses,
              plazo_dias: base.plazo_dias ?? desdeLista?.plazo_dias,
              tipo: base.tipo || base.tipo_contrato || desdeLista?.tipo,
              fecha_fin: base.fecha_fin || desdeLista?.fecha_fin,
              lugar_firma: base.lugar_firma || desdeLista?.lugar_firma,
              titulo: base.titulo || desdeLista?.titulo,
            },
            plantillasCargadas
          );
          renovarAplicado = true;
          onRenovacionAplicada?.();
        }
      }

      if (!renovarAplicado) {
        setMostrarFormularioNuevo(!soloHistorial && (!contratos || contratos.length === 0));
      }
      setLoading(false);
    };

    cargarDatos();
  }, [isOpen, empleado?.id, empresaId, soloHistorial, contratoARenovar]);

  useEffect(() => {
    if (!isOpen || !empresaId) {
      setLogoEmpresaUrl(null);
      return undefined;
    }

    let activo = true;

    obtenerLogoEmpresaParaVista(empresaId).then((src) => {
      if (activo) setLogoEmpresaUrl(src || null);
    });

    return () => {
      activo = false;
    };
  }, [isOpen, empresaId]);

  const htmlVistaPreviaAsignacion = useMemo(() => {
    if (!vistaPrevia?.introduccion && !vistaPrevia?.articulos?.length) return '';

    return construirContenidoVistaPreviaContratoResuelto({
      tipo: vistaPrevia.plantilla?.tipo || tipoContrato,
      titulo: vistaPrevia.plantilla?.titulo,
      introduccion: vistaPrevia.introduccion,
      articulos: vistaPrevia.articulos,
      mapa: vistaPrevia.variables_resueltas || {},
      fuente: vistaPrevia.plantilla?.fuente,
    });
  }, [vistaPrevia, tipoContrato]);

  const htmlVistaPreviaContratoDetalle = useMemo(() => {
    if (!contratoDetalle) return '';

    return construirContenidoVistaPreviaContratoResuelto({
      tipo: contratoDetalle.tipo,
      titulo: contratoDetalle.titulo,
      introduccion: contratoDetalle.introduccion,
      articulos: contratoDetalle.articulos,
      mapa: contratoDetalle.variables_resueltas || {},
      fuente: contratoDetalle.fuente,
    });
  }, [contratoDetalle]);

  const htmlVistaPreviaEdicion = useMemo(() => {
    if (!contratoEditando || (!vistaPrevia?.introduccion && !vistaPrevia?.articulos?.length)) return '';

    return construirContenidoVistaPreviaContratoResuelto({
      tipo: vistaPrevia.plantilla?.tipo || contratoEditando.tipo,
      titulo: vistaPrevia.plantilla?.titulo || contratoEditando.titulo,
      introduccion: vistaPrevia.introduccion,
      articulos: vistaPrevia.articulos,
      mapa: vistaPrevia.variables_resueltas || {},
      fuente: vistaPrevia.plantilla?.fuente || contratoEditando.fuente,
    });
  }, [vistaPrevia, contratoEditando]);

  const plantillasCompatibles = useMemo(() => {
    if (!tipoContrato) return [];
    return plantillas.filter(
      (item) => item.activo !== false && item.tipo === tipoContrato
    );
  }, [plantillas, tipoContrato]);

  const handleTipoContratoChange = (value) => {
    setTipoContrato(value);
    setPlantillaId('');
    setVistaPrevia(null);
    setContratoDetalle(null);
    setContratoEditando(null);
  };

  const iniciarEdicion = (contrato) => {
    setContratoEditando(contrato);
    setContratoDetalle(null);
    setMostrarFormularioNuevo(false);
    setVistaPrevia(null);
    setTipoContrato(contrato.tipo || '');
    setPlantillaId(String(contrato.plantilla_contrato_id || ''));
    const inicio = contrato.fecha_inicio ? String(contrato.fecha_inicio).split('T')[0] : '';
    const fin = contrato.fecha_fin ? String(contrato.fecha_fin).split('T')[0] : '';
    setFechaInicio(inicio);
    setPlazoMeses(contrato.plazo_meses != null ? String(contrato.plazo_meses) : '');
    setFechaFin(fin);
    setModoPlazo(
      (Number(contrato.plazo_dias) > 0) || (fin && !esPlazoSoloMeses(inicio, contrato.plazo_meses, fin))
        ? 'fecha_fin'
        : 'meses'
    );
    setFechaFirma(contrato.fecha_firma ? String(contrato.fecha_firma).split('T')[0] : '');
    setLugarFirma(contrato.lugar_firma || '');
    setError('');
  };

  const cancelarEdicion = () => {
    setContratoEditando(null);
    setVistaPrevia(null);
    resetFormularioNuevo();
    setError('');
  };

  const mensajeErrorPlazo = () => {
    if (!fechaInicio) return 'Ingresa la fecha de inicio del contrato';
    if (modoPlazo === 'fecha_fin' && !fechaFin) return 'Ingresa la fecha de fin del contrato';
    if (modoPlazo === 'fecha_fin' && fechaFin < fechaInicio) {
      return 'La fecha de fin no puede ser anterior al inicio';
    }
    if (modoPlazo === 'meses' && !plazoMeses) {
      return 'Ingresa el plazo en meses o elige fecha de fin exacta';
    }
    return '';
  };

  const generarVistaPrevia = async (idPlantilla = plantillaId) => {
    if (!idPlantilla || !empleado?.id) {
      setError('Selecciona una plantilla');
      return;
    }

    const errorPlazo = mensajeErrorPlazo();
    if (errorPlazo) {
      setError(errorPlazo);
      return;
    }

    setLoading(true);
    setError('');
    setContratoDetalle(null);

    const response = await contratosEmpleadoService.vistaPrevia(construirPayload(idPlantilla));
    setLoading(false);

    if (response.success) {
      setVistaPrevia(response.data);
    } else {
      setVistaPrevia(null);
      setError(response.error || 'No se pudo generar la vista previa');
    }
  };

  const generarVistaPreviaEdicion = async () => {
    if (!contratoEditando?.plantilla_contrato_id || !empleado?.id) {
      setError('No se pudo identificar la plantilla del contrato');
      return;
    }

    const errorPlazo = mensajeErrorPlazo();
    if (errorPlazo) {
      setError(errorPlazo);
      return;
    }

    setLoading(true);
    setError('');

    const payloadEdicion = construirPayloadEdicion();
    const response = await contratosEmpleadoService.vistaPrevia({
      empleado_id: empleado.id,
      ...payloadEdicion,
    });
    setLoading(false);

    if (response.success) {
      setVistaPrevia(response.data);
    } else {
      setVistaPrevia(null);
      setError(response.error || 'No se pudo generar la vista previa');
    }
  };

  const handlePlantillaChange = async (value) => {
    setPlantillaId(value);
    setVistaPrevia(null);
    setContratoDetalle(null);

    if (value && !mensajeErrorPlazo()) {
      setLoading(true);
      setError('');
      const response = await contratosEmpleadoService.vistaPrevia(construirPayload(value));
      setLoading(false);
      if (response.success) {
        setVistaPrevia(response.data);
      }
    }
  };

  const handleAsignar = async () => {
    if (!plantillaId) {
      setError('Selecciona una plantilla');
      return;
    }

    const errorPlazoAsignar = mensajeErrorPlazo();
    if (errorPlazoAsignar) {
      setError(errorPlazoAsignar);
      return;
    }

    const payload = construirPayload();
    const solapados = buscarContratosSolapados(
      {
        fecha_inicio: fechaInicio,
        plazo_meses: payload.plazo_meses,
        plazo_dias: payload.plazo_dias,
        fecha_fin: payload.fecha_fin,
      },
      contratosAsignados
    );
    if (solapados.length > 0 && !window.confirm(mensajeConfirmacionSolape(solapados))) {
      return;
    }

    setGuardando(true);
    setError('');

    const response = await contratosEmpleadoService.asignar(payload);
    setGuardando(false);

    if (response.success) {
      await cargarContratos();
      resetFormularioNuevo();
      setMostrarFormularioNuevo(false);
      setRenovandoDesde(null);
      const msg = response.advertencia_solapamiento
        ? `${response.message}. ${response.advertencia_solapamiento}`
        : response.message;
      onSuccess?.(msg);
      return;
    }

    setError(response.error || 'No se pudo asignar el contrato');
  };

  const handleGuardarEdicion = async () => {
    if (!contratoEditando?.id) {
      setError('No se pudo identificar el contrato a editar');
      return;
    }

    const errorPlazoEdicion = mensajeErrorPlazo();
    if (errorPlazoEdicion) {
      setError(errorPlazoEdicion);
      return;
    }

    const payloadEdicion = construirPayloadEdicion();
    const solapados = buscarContratosSolapados(
      {
        fecha_inicio: fechaInicio,
        plazo_meses: payloadEdicion.plazo_meses,
        plazo_dias: payloadEdicion.plazo_dias,
        fecha_fin: payloadEdicion.fecha_fin,
      },
      contratosAsignados,
      contratoEditando.id
    );
    if (solapados.length > 0 && !window.confirm(mensajeConfirmacionSolape(solapados))) {
      return;
    }

    setGuardando(true);
    setError('');

    const response = await contratosEmpleadoService.actualizar(
      contratoEditando.id,
      payloadEdicion
    );
    setGuardando(false);

    if (response.success) {
      await cargarContratos();
      setContratoEditando(null);
      setVistaPrevia(null);
      resetFormularioNuevo();
      const msg = response.advertencia_solapamiento
        ? `${response.message}. ${response.advertencia_solapamiento}`
        : response.message;
      onSuccess?.(msg);
      return;
    }

    setError(response.error || 'No se pudo actualizar el contrato');
  };

  const cambiarEstadoContrato = async (contrato, nuevoEstado) => {
    setLoading(true);
    setError('');
    const response = await contratosEmpleadoService.actualizarEstado(contrato.id, nuevoEstado);
    setLoading(false);

    if (response.success) {
      await cargarContratos();
      if (contratoDetalle?.id === contrato.id) {
        setContratoDetalle(response.data);
      }
      onSuccess?.(response.message);
    } else {
      setError(response.error || 'No se pudo actualizar el estado');
    }
  };

  const eliminarContrato = async (contrato) => {
    const titulo = contrato.titulo || `Contrato #${contrato.id}`;
    const periodo = `${formatearFecha(contrato.fecha_inicio)} – ${formatearFecha(contrato.fecha_fin)}`;
    if (!window.confirm(`¿Eliminar «${titulo}» (${periodo})?\n\nEsta acción no se puede deshacer.`)) {
      return;
    }

    setLoading(true);
    setError('');
    const response = await contratosEmpleadoService.eliminar(contrato.id);
    setLoading(false);

    if (response.success) {
      if (contratoDetalle?.id === contrato.id) {
        setContratoDetalle(null);
      }
      if (contratoEditando?.id === contrato.id) {
        setContratoEditando(null);
        setVistaPrevia(null);
        resetFormularioNuevo();
      }
      const restantes = await cargarContratos();
      if (!soloHistorial && (!restantes || restantes.length === 0)) {
        setMostrarFormularioNuevo(true);
      }
      onSuccess?.(response.message);
      return;
    }

    setError(response.error || 'No se pudo eliminar el contrato');
  };

  const verContrato = (contrato) => {
    setContratoDetalle(contrato);
    setVistaPrevia(null);
    setMostrarFormularioNuevo(false);
  };

  const descargarPdfContrato = async (contrato) => {
    if (!contrato?.id || !empresaId) {
      setError('No se pudo identificar la empresa para generar el PDF');
      return;
    }

    setGenerandoPdf(true);
    setError('');

    try {
      let contratoCompleto = contrato;
      if (!contrato.articulos?.length) {
        const response = await contratosEmpleadoService.obtenerContrato(contrato.id);
        if (!response.success) {
          throw new Error(response.error || 'No se pudo cargar el contrato');
        }
        contratoCompleto = response.data;
      }

      const firmaBase64 = await obtenerFirmaEmpresaBase64(empresaId);
      const logoBase64 = await obtenerLogoEmpresaBase64(empresaId);
      await exportarContratoEmpleadoPdf({
        empresa: empresa || { id: empresaId },
        empleado,
        contrato: contratoCompleto,
        firmaEmpresaBase64: firmaBase64,
        logoEmpresaBase64: logoBase64,
      });
      onSuccess?.('PDF del contrato generado correctamente');
    } catch (err) {
      setError(err.message || 'No se pudo generar el PDF del contrato');
    } finally {
      setGenerandoPdf(false);
    }
  };

  if (!isOpen || !empleado) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black bg-opacity-50">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-5xl max-h-[92vh] overflow-hidden flex flex-col">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h2 className="text-xl font-bold text-gray-900">
              {soloHistorial ? 'Historial de contratos' : 'Contratos del empleado'}
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              {empleado.nombre_completo}
              {empleado.tipo_contratacion ? ` · Modalidad: ${empleado.tipo_contratacion}` : ''}
            </p>
          </div>
          <button type="button" onClick={onClose} className="text-gray-400 hover:text-gray-600">✕</button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-6">
          {renovandoDesde && (
            <div
              role="status"
              className="rounded-lg border border-sky-300 bg-sky-50 px-4 py-3 text-sm text-sky-950"
            >
              <p className="font-semibold">Renovando contrato vencido</p>
              <p className="mt-1 text-sky-900">
                Se creará un contrato nuevo a partir de «{renovandoDesde.titulo || 'contrato'}»
                {renovandoDesde.fecha_fin
                  ? ` (venció el ${formatearFecha(renovandoDesde.fecha_fin)})`
                  : ''}
                . Revisa fechas y plantilla, luego asigna.
              </p>
            </div>
          )}

          {paresSolapados.length > 0 && (
            <div
              role="alert"
              className="rounded-lg border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-950"
            >
              <p className="font-semibold">
                Atención: este empleado tiene {paresSolapados.length} solapamiento(s) de periodos
              </p>
              <ul className="mt-2 list-disc pl-5 space-y-1 text-amber-900">
                {paresSolapados.map((par) => (
                  <li key={`${par.contrato_a.id}-${par.contrato_b.id}`}>
                    «{par.contrato_a.titulo || `Contrato #${par.contrato_a.id}`}» y «
                    {par.contrato_b.titulo || `Contrato #${par.contrato_b.id}`}» coinciden en fechas
                    ({formatearFecha(par.contrato_a.fecha_inicio)} –{' '}
                    {formatearFecha(par.contrato_a.fecha_fin)} /{' '}
                    {formatearFecha(par.contrato_b.fecha_inicio)} –{' '}
                    {formatearFecha(par.contrato_b.fecha_fin)})
                  </li>
                ))}
              </ul>
              <p className="mt-2 text-amber-800">
                Puede ser válido (p. ej. convenio + contrato laboral). Revísalo antes de firmar.
              </p>
            </div>
          )}

          <div className="border border-gray-200 rounded-lg overflow-hidden">
            <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between">
              <h3 className="font-semibold text-gray-900">
                {soloHistorial ? 'Contratos registrados' : 'Contratos asignados'}
              </h3>
              <span className="text-sm text-gray-500">{contratosAsignados.length} registro(s)</span>
            </div>

            {loading && contratosAsignados.length === 0 ? (
              <div className="p-6 text-center text-gray-500">Cargando contratos...</div>
            ) : contratosAsignados.length === 0 ? (
              <div className="p-6 text-center text-gray-500">
                {soloHistorial
                  ? 'Este empleado no tiene contratos registrados.'
                  : 'Este empleado aún no tiene contratos asignados.'}
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Contrato</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Inicio</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Plazo</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Fin</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Estado</th>
                      <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Acciones</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {contratosAsignados.map((contrato) => {
                      const estado = contrato.estado === 'asignado' ? 'pendiente' : contrato.estado;
                      const enSolape =
                        contrato.en_solapamiento === true || idsEnSolape.has(Number(contrato.id));
                      const vencido = contratoEstaVencido(contrato);
                      return (
                        <tr
                          key={contrato.id}
                          className={
                            vencido
                              ? 'bg-red-50 hover:bg-red-100/70'
                              : enSolape
                                ? 'bg-amber-50 hover:bg-amber-100/80'
                                : 'hover:bg-gray-50'
                          }
                        >
                          <td className="px-4 py-3 text-sm text-gray-900">
                            <div className="flex flex-col gap-1">
                              <span>{contrato.titulo}</span>
                              {vencido && (
                                <span className="inline-flex w-fit px-2 py-0.5 text-[11px] font-semibold rounded-full bg-red-200 text-red-900">
                                  Vencido
                                </span>
                              )}
                              {!vencido && enSolape && (
                                <span className="inline-flex w-fit px-2 py-0.5 text-[11px] font-semibold rounded-full bg-amber-200 text-amber-900">
                                  Periodo solapado
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700">{formatearFecha(contrato.fecha_inicio)}</td>
                          <td className="px-4 py-3 text-sm text-gray-700">
                            {plazoDesdeContrato(contrato)}
                          </td>
                          <td className="px-4 py-3 text-sm text-gray-700">{formatearFecha(contrato.fecha_fin)}</td>
                          <td className="px-4 py-3">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${badgeEstado(estado)}`}>
                              {estado === 'firmado' ? 'Firmado' : 'Pendiente'}
                            </span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="flex flex-wrap gap-2">
                              <button
                                type="button"
                                onClick={() => verContrato(contrato)}
                                className="text-xs text-indigo-600 hover:text-indigo-800"
                              >
                                Ver
                              </button>
                              <button
                                type="button"
                                onClick={() => descargarPdfContrato(contrato)}
                                disabled={generandoPdf}
                                className="text-xs text-blue-600 hover:text-blue-800 disabled:opacity-50"
                              >
                                {generandoPdf ? 'Generando...' : 'PDF'}
                              </button>
                              {!soloHistorial && vencido && (
                                <button
                                  type="button"
                                  onClick={() => iniciarRenovacion(contrato)}
                                  className="text-xs font-semibold text-sky-700 hover:text-sky-900"
                                >
                                  Renovar
                                </button>
                              )}
                              {!soloHistorial && (
                                <button
                                  type="button"
                                  onClick={() => iniciarEdicion(contrato)}
                                  className="text-xs text-gray-600 hover:text-gray-800"
                                >
                                  Editar
                                </button>
                              )}
                              {!soloHistorial && (
                                estado !== 'firmado' ? (
                                  <button
                                    type="button"
                                    onClick={() => cambiarEstadoContrato(contrato, 'firmado')}
                                    className="text-xs text-green-600 hover:text-green-800"
                                  >
                                    Marcar firmado
                                  </button>
                                ) : (
                                  <button
                                    type="button"
                                    onClick={() => cambiarEstadoContrato(contrato, 'pendiente')}
                                    className="text-xs text-amber-600 hover:text-amber-800"
                                  >
                                    Marcar pendiente
                                  </button>
                                )
                              )}
                              {!soloHistorial && (
                                <button
                                  type="button"
                                  onClick={() => eliminarContrato(contrato)}
                                  disabled={loading}
                                  className="text-xs text-red-600 hover:text-red-800 disabled:opacity-50"
                                >
                                  Eliminar
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {!soloHistorial && contratoEditando && (
            <div className="space-y-4 border border-amber-200 rounded-lg p-4 bg-amber-50/40">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <h3 className="text-lg font-semibold text-gray-900">Editar contrato</h3>
                  <p className="text-sm text-gray-600 mt-0.5">{contratoEditando.titulo}</p>
                </div>
                <button
                  type="button"
                  onClick={cancelarEdicion}
                  className="text-sm text-gray-500 hover:text-gray-700"
                >
                  Cancelar edición
                </button>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                <div className="lg:col-span-2">
                  <label className="block text-sm font-medium text-gray-700 mb-2">Plantilla</label>
                  <input
                    type="text"
                    value={contratoEditando.titulo || ''}
                    disabled
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg bg-gray-100 text-gray-700"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Fecha de inicio *</label>
                  <input
                    type="date"
                    value={fechaInicio}
                    onChange={(e) => setFechaInicio(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <CamposPlazoContrato
                  modoPlazo={modoPlazo}
                  onCambiarModo={cambiarModoPlazo}
                  fechaInicio={fechaInicio}
                  plazoMeses={plazoMeses}
                  onPlazoMeses={setPlazoMeses}
                  fechaFin={fechaFin}
                  onFechaFin={setFechaFin}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Fecha de firma</label>
                  <input
                    type="date"
                    value={fechaFirma}
                    onChange={(e) => setFechaFirma(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">Lugar de firma</label>
                  <input
                    type="text"
                    value={lugarFirma}
                    onChange={(e) => setLugarFirma(e.target.value)}
                    placeholder="Ej: Arequipa"
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
              </div>

              <div className="flex justify-end">
                <button
                  type="button"
                  onClick={generarVistaPreviaEdicion}
                  disabled={loading}
                  className="px-4 py-2 text-sm border border-indigo-300 text-indigo-700 rounded-lg hover:bg-indigo-50 disabled:opacity-50"
                >
                  Actualizar vista previa
                </button>
              </div>
            </div>
          )}

          {!soloHistorial && !contratoEditando && (
            !mostrarFormularioNuevo ? (
            <button
              type="button"
              onClick={() => {
                resetFormularioNuevo();
                setContratoDetalle(null);
                setContratoEditando(null);
                setMostrarFormularioNuevo(true);
              }}
              className="px-4 py-2 text-sm bg-indigo-600 text-white rounded-lg hover:bg-indigo-700"
            >
              + Asignar nuevo contrato
            </button>
          ) : (
            <>
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Nuevo contrato</h3>
                {contratosAsignados.length > 0 && (
                  <button
                    type="button"
                    onClick={() => {
                      setMostrarFormularioNuevo(false);
                      resetFormularioNuevo();
                    }}
                    className="text-sm text-gray-500 hover:text-gray-700"
                  >
                    Cancelar nuevo
                  </button>
                )}
              </div>

              {tiposContrato.length === 0 ? (
                <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4 text-sm text-yellow-800">
                  No hay tipos de contrato configurados. Configúralos en <strong>Configuración → Tipos de Contratación</strong>.
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Tipo de contrato *</label>
                      <select
                        value={tipoContrato}
                        onChange={(e) => handleTipoContratoChange(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                      >
                        <option value="">Seleccionar tipo...</option>
                        {tiposContrato.map((tipo) => (
                          <option key={tipo} value={tipo}>{tipo}</option>
                        ))}
                      </select>
                    </div>
                    <div className="lg:col-span-2">
                      <label className="block text-sm font-medium text-gray-700 mb-2">Plantilla</label>
                      <select
                        value={plantillaId}
                        onChange={(e) => handlePlantillaChange(e.target.value)}
                        disabled={!tipoContrato || plantillasCompatibles.length === 0}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 disabled:bg-gray-100"
                      >
                        <option value="">
                          {!tipoContrato
                            ? 'Selecciona primero el tipo de contrato'
                            : plantillasCompatibles.length === 0
                              ? 'No hay plantillas activas para este tipo'
                              : 'Seleccionar plantilla'}
                        </option>
                        {plantillasCompatibles.map((plantilla) => (
                          <option key={plantilla.id} value={plantilla.id}>
                            {plantilla.titulo}
                          </option>
                        ))}
                      </select>
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Fecha de inicio *</label>
                      <input
                        type="date"
                        value={fechaInicio}
                        onChange={(e) => setFechaInicio(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                      />
                    </div>
                    <CamposPlazoContrato
                      modoPlazo={modoPlazo}
                      onCambiarModo={cambiarModoPlazo}
                      fechaInicio={fechaInicio}
                      plazoMeses={plazoMeses}
                      onPlazoMeses={setPlazoMeses}
                      fechaFin={fechaFin}
                      onFechaFin={setFechaFin}
                    />
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Fecha de firma</label>
                      <input
                        type="date"
                        value={fechaFirma}
                        onChange={(e) => setFechaFirma(e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">Lugar de firma</label>
                      <input
                        type="text"
                        value={lugarFirma}
                        onChange={(e) => setLugarFirma(e.target.value)}
                        placeholder="Ej: Arequipa"
                        className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500"
                      />
                    </div>
                  </div>

                  <div className="flex justify-end">
                    <button
                      type="button"
                      onClick={() => generarVistaPrevia()}
                      disabled={!plantillaId || loading}
                      className="px-4 py-2 text-sm border border-indigo-300 text-indigo-700 rounded-lg hover:bg-indigo-50 disabled:opacity-50"
                    >
                      Actualizar vista previa
                    </button>
                  </div>
                </>
              )}
            </>
          )
          )}

          {contratoDetalle && (
            <div className="space-y-4">
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="px-4 py-3 bg-gray-50 border-b border-gray-200 flex items-center justify-between gap-3">
                  <div>
                    <h3 className="font-semibold text-gray-900">{contratoDetalle.titulo}</h3>
                    <p className="text-sm text-gray-600">
                      {formatearFecha(contratoDetalle.fecha_inicio)} → {formatearFecha(contratoDetalle.fecha_fin)}
                    </p>
                  </div>
                  <div className="flex items-center gap-2">
                    <button
                      type="button"
                      onClick={() => descargarPdfContrato(contratoDetalle)}
                      disabled={generandoPdf}
                      className="px-3 py-1.5 text-xs bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50"
                    >
                      {generandoPdf ? 'Generando PDF...' : 'Descargar PDF'}
                    </button>
                    <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${badgeEstado(contratoDetalle.estado)}`}>
                      {contratoDetalle.estado === 'firmado' ? 'Firmado' : 'Pendiente'}
                    </span>
                  </div>
                </div>
              </div>

              <VistaPreviaContratoPaginada
                titulo="Contrato asignado"
                htmlContenido={htmlVistaPreviaContratoDetalle}
                logoSrc={logoEmpresaUrl}
                fuente={contratoDetalle.fuente}
              />
            </div>
          )}

          {htmlVistaPreviaEdicion && (
            <VistaPreviaContratoPaginada
              titulo="Vista previa del contrato editado"
              subtitulo={contratoEditando?.titulo}
              htmlContenido={htmlVistaPreviaEdicion}
              logoSrc={logoEmpresaUrl}
              fuente={vistaPrevia?.plantilla?.fuente || contratoEditando?.fuente}
            />
          )}

          {htmlVistaPreviaAsignacion && !contratoEditando && (
            <VistaPreviaContratoPaginada
              titulo="Vista previa del contrato"
              subtitulo={vistaPrevia.plantilla?.titulo}
              htmlContenido={htmlVistaPreviaAsignacion}
              logoSrc={logoEmpresaUrl}
              fuente={vistaPrevia.plantilla?.fuente}
            />
          )}
        </div>

        {error && (
          <div className="px-6 py-3 bg-red-50 border-t border-red-100 text-sm text-red-700">
            {error}
          </div>
        )}

        <div className="px-6 py-4 border-t border-gray-200 flex justify-end gap-3">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            Cerrar
          </button>
          {mostrarFormularioNuevo && !soloHistorial && !contratoEditando && (
            <button
              type="button"
              onClick={handleAsignar}
              disabled={guardando || !plantillaId || !tipoContrato}
              className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:bg-gray-300"
            >
              {guardando
                ? 'Renovando...'
                : renovandoDesde
                  ? 'Confirmar renovación'
                  : 'Asignar contrato'}
            </button>
          )}
          {contratoEditando && !soloHistorial && (
            <button
              type="button"
              onClick={handleGuardarEdicion}
              disabled={guardando || !fechaInicio}
              className="px-4 py-2 bg-amber-600 text-white rounded-lg hover:bg-amber-700 disabled:bg-gray-300"
            >
              {guardando ? 'Guardando...' : 'Guardar cambios'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
};

export default AsignarContratoModal;
